<template>
  <JobProfileSettingsSection
    id="job-levels"
    title="Job Levels"
  >
    <template #description>
      Manage your organization's job levels by customizing how each level is shortened.<br>
      You can drag to arrange them from most junior to most senior.
      <a
        href="#"
        data-beacon-article-sidebar="69cd28049207e139315c3eb2"
        @click.stop
      >Learn more about job levels</a>.
    </template>

    <div class="column-header d-flex align-center ga-6 w-100 mb-2">
      <span class="column-header-label level-col-level">Level</span>
      <span class="column-header-label level-col-shortened">Shortened</span>
    </div>

    <draggable
      v-model="levels"
      item-key="rowKey"
      handle=".drag-handle"
      tag="div"
      class="draggable-list d-flex flex-column ga-2 w-100"
      ghost-class="ghost"
      :disabled="componentLoading"
      @end="syncSortOrders"
    >
      <template #item="{ element }">
        <JobProfileLevelRow
          :level="element"
          :error="getRowError(element.rowKey)"
          :can-edit="canEditLevels"
          :disabled="componentLoading"
          @update:name="setLevelName(element.rowKey, $event)"
          @update:alias="setLevelAlias(element.rowKey, $event)"
          @blur="handleNameBlur(element.rowKey)"
        />
      </template>
    </draggable>

    <DpplNewButton
      v-if="canEditLevels"
      class="add-level-button text-button-3 text-brandPrimary bg-white mt-3"
      variant="outlined"
      height="32"
      :disabled="componentLoading"
      @click="addLevel"
    >
      Add Level
    </DpplNewButton>
  </JobProfileSettingsSection>
</template>

<script setup>
import { computed, ref } from 'vue';
import { storeToRefs } from 'pinia';
import draggable from 'vuedraggable';
import DpplNewButton from '@/components/datapeople/DpplNewButton.vue';
import JobProfileLevelRow from '@/components/settings/components/JobProfileLevelRow.vue';
import JobProfileSettingsSection from '@/components/settings/components/JobProfileSettingsSection.vue';
import { useAppAuthData } from '@/stores/app/authData';
import { useAppLoading } from '@/stores/app/loading';
import { isLevelNameDuplicateError } from '@/helpers/jobProfileSettingsValidation';
import { useJobProfileSettings } from '@/stores/settings/jobProfileSettings';

const jobProfileSettings = useJobProfileSettings();
const { levels, levelErrors } = storeToRefs(jobProfileSettings);
const {
  syncSortOrders,
  setLevelAlias,
  setLevelName,
  addLevel,
} = jobProfileSettings;

const authData = useAppAuthData();
const { isStandaloneJobNav } = storeToRefs(authData);
const { checkRbacPermissions } = authData;
const { componentLoading } = storeToRefs(useAppLoading());

const canEditLevels = computed(() => (
  isStandaloneJobNav.value
  && checkRbacPermissions(['createJobProfileLevel'])
));

const blurredRowKeys = ref({});
const lastBlurredRowKey = ref(null);

const getRowError = (rowKey) => {
  if (!blurredRowKeys.value[rowKey]) return '';

  const message = levelErrors.value[rowKey] || '';

  if (!message) return '';

  if (
    isLevelNameDuplicateError(levels.value, rowKey)
    && rowKey !== lastBlurredRowKey.value
  ) {
    return '';
  }

  return message;
};

const handleNameBlur = (rowKey) => {
  lastBlurredRowKey.value = rowKey;
  blurredRowKeys.value = { ...blurredRowKeys.value, [rowKey]: true };
};
</script>

<style scoped lang="less">
.column-header-label {
  font-size: 13px;
  font-weight: 600;
  line-height: 1.5;
  color: @text-dark;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

.level-col-level {
  flex: 0 0 400px;
  width: 400px;
}

.level-col-shortened {
  flex: 0 0 100px;
  width: 100px;
}

.add-level-button {
  min-width: 0;
}

:deep(.ghost) {
  opacity: 0.55;
}
</style>
